/**
 * PATH: packages/ui/src/kanban/components/card-detail/CardLinkedEmailSection.tsx
 * FILE_ID: UI-KANBAN-CMP-CARD-LINKED-EMAIL-001
 * PURPOSE: Surface the card's linked email(s) INLINE on the Card tab, in a box
 *          ABOVE the description, reusing the EXACT inbox <EmailRow> so a card
 *          reads consistently with the Email Hub. A card can carry MANY emails
 *          (one `primary_email` + N `related_emails`): every linked email is
 *          listed (each independently expandable via <LinkedEmailRow>), and an
 *          "Attach another email" affordance stays available after the first
 *          one so operators can keep adding emails in place.
 * WHY:     Operator 2026-06-22 (screenshot): "Put the email in a box above
 *          description ... reuse the exact email box we see in inbox ... if email
 *          is not there allow directly searching and attaching an email."
 *          Operator 2026-06-29: "the ability to add MULTIPLE emails in the
 *          kanban card is not working properly" — only the primary email ever
 *          rendered and, once one email was linked, the attach affordance
 *          vanished, so a 2nd/3rd email could neither be added here nor seen.
 * CONSTRAINT: CardDetailDrawer lives in @quotech/ui, which CANNOT import the
 *          email-hub's fully-wired FolderEmailRow (that would create a package
 *          dependency cycle: email-hub depends on ui, not the reverse). The
 *          in-package <EmailRow> primitive IS the same visual unit the inbox
 *          renders, so we reuse THAT and feed it from the kanban email adapter.
 * RELATED:
 *   - card-detail/LinkedEmailRow.tsx: one collapsible linked-email row.
 *   - card-detail/linkedEmails.ts: orderLinkedEmailIds (primary-first, deduped).
 *   - card-detail/emailRowMapping.ts: adapter → <EmailRow> mapping helpers.
 *   - components/email/EmailRow.tsx: the universal inbox row this reuses.
 *   - KanbanEmailAllocationRail.tsx / KanbanBoardView.tsx: the drag-to-card flow
 *     whose `createAllocation({ source: 'USER_MANUAL' })` we mirror here.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, type QueryClient } from '@tanstack/react-query'
import { Loader2, Paperclip, Search, X } from 'lucide-react'

import { EmailRow } from '../../../components/email/EmailRow'
import { LinkedEmailRow } from './LinkedEmailRow'
import { orderLinkedEmailIds } from './linkedEmails'
import { NEUTRAL_CATEGORY, swallow, toEmailMessage } from './emailRowMapping'
import type { KanbanApi, KanbanEmailAdapter } from '../../api'
import type { Card } from '../../types'

export interface CardLinkedEmailSectionProps {
  card: Card
  api: KanbanApi
  emailAdapter?: KanbanEmailAdapter
  /** Parent board id — scopes the post-attach cards invalidation. */
  boardId?: string
  qc: QueryClient
  /** Bubble the refreshed card up so the parent board tile reflects the
   *  newly-linked email immediately (mirrors `pushFocusedCardUpdate`). */
  onChanged?: (card: Card) => void
  /** Open the full email reader (the drawer's Email tab). Pass a specific
   *  linked email id to open THAT one (BUG-2026-06-29: opening from a
   *  non-primary row used to land on the primary); omit it for the header
   *  button (opens the primary / first). */
  onOpenEmail: (emailId?: string) => void
}

export function CardLinkedEmailSection({
  card,
  api,
  emailAdapter,
  boardId,
  qc,
  onChanged,
  onOpenEmail,
}: CardLinkedEmailSectionProps) {
  const canSearch = !!emailAdapter?.listInboxEmails

  // Every email linked to the card — primary first, then related, deduped.
  const linkedIds = useMemo(
    () => orderLinkedEmailIds(card.primary_email, card.related_emails),
    [card.primary_email, card.related_emails],
  )
  const linkedSet = useMemo(() => new Set(linkedIds), [linkedIds])
  const hasLinked = linkedIds.length > 0

  // Attach mode — reachable whether or not emails are already linked.
  const [attaching, setAttaching] = useState(false)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 250)
    return () => window.clearTimeout(t)
  }, [search])

  const searchQ = useQuery({
    queryKey: ['kanban', 'card-attach-email-search', debounced],
    queryFn: () =>
      emailAdapter!.listInboxEmails!({ search: debounced || undefined, limit: 8 }),
    enabled: attaching && canSearch,
    staleTime: 10_000,
  })

  const attachMut = useMutation({
    mutationFn: async (emailId: string): Promise<Card> => {
      // The CORRECT linkage path is POST /cards/<id>/link-email/
      // (`link_email_to_card`): it sets `primary_email` on a card with none
      // and otherwise threads the email into `related_emails`. A bare
      // `createAllocation` (POST /allocations/) only grants visibility — it
      // never touches the card's email fields, so attaching an email that way
      // left it invisible on the card (the root of "adding multiple emails
      // isn't working"). The endpoint returns the refreshed card.
      if (api.linkEmailToCard) {
        const res = await api.linkEmailToCard({
          cardId: card.id,
          emailId,
          source: 'USER_MANUAL',
        })
        return res?.card ?? (await api.getCard(card.id))
      }
      // Legacy fallback for hosts that haven't wired the link-email endpoint:
      // create the allocation, then re-read the card.
      await api.createAllocation({
        organisation: card.organisation,
        email: emailId,
        card: card.id,
        source: 'USER_MANUAL',
      })
      return api.getCard(card.id)
    },
    onSuccess: (updated) => {
      setAttaching(false)
      setSearch('')
      setDebounced('')
      onChanged?.(updated)
      qc.invalidateQueries({
        queryKey: boardId ? ['kanban', 'cards', boardId] : ['kanban', 'cards'],
      })
    },
  })

  // Arc B — link-lifecycle mutations (detach + promote). Server owns the
  // three-mechanism consistency (allocation revoke + M2M prune + primary
  // re-point); the FE just refreshes from the returned card.
  const detachMut = useMutation({
    mutationFn: async (emailId: string): Promise<Card> => {
      const res = await api.unlinkEmailFromCard!({ cardId: card.id, emailId })
      return res?.card ?? (await api.getCard(card.id))
    },
    onSuccess: (updated) => {
      onChanged?.(updated)
      qc.invalidateQueries({
        queryKey: boardId ? ['kanban', 'cards', boardId] : ['kanban', 'cards'],
      })
      void qc.invalidateQueries({ queryKey: ['kanban', 'card-linked-email'] })
    },
  })
  const makePrimaryMut = useMutation({
    mutationFn: async (emailId: string): Promise<Card> => {
      const res = await api.setPrimaryEmail!({ cardId: card.id, emailId })
      return res?.card ?? (await api.getCard(card.id))
    },
    onSuccess: (updated) => {
      onChanged?.(updated)
      qc.invalidateQueries({
        queryKey: boardId ? ['kanban', 'cards', boardId] : ['kanban', 'cards'],
      })
    },
  })

  // Nothing linked and no way to attach → don't add an empty section.
  if (!hasLinked && !canSearch) return null

  // Hide already-linked emails from the picker (the link is idempotent on the
  // BE either way, but the picker should only offer genuinely NEW emails).
  const attachResults = (searchQ.data ?? []).filter((e) => !linkedSet.has(e.id))

  return (
    <section aria-label="Linked email">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
          {linkedIds.length > 1 ? `Emails · ${linkedIds.length}` : 'Email'}
        </h3>
        {hasLinked && (
          <button
            type="button"
            onClick={() => onOpenEmail()}
            className="text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Open full email →
          </button>
        )}
      </div>

      {/* Linked emails — one collapsible inbox row each. */}
      {hasLinked && emailAdapter && (
        <div className="space-y-1.5">
          {linkedIds.map((id, idx) => (
            <LinkedEmailRow
              key={id}
              emailId={id}
              adapter={emailAdapter}
              isPrimary={idx === 0}
              fallbackSubject={card.title}
              onOpenInTab={() => onOpenEmail(id)}
              cardId={card.id}
              onChanged={() => {
                // Reader-side mutation (tag/send/archive) — refresh the card
                // and every linked-email preview this section renders.
                void qc.invalidateQueries({ queryKey: ['kanban', 'card-linked-email'] })
                void api.getCard(card.id).then((c) => onChanged?.(c))
              }}
              onDetach={
                api.unlinkEmailFromCard
                  ? () => detachMut.mutate(id)
                  : undefined
              }
              detachBusy={detachMut.isPending && detachMut.variables === id}
              onMakePrimary={
                api.setPrimaryEmail && idx !== 0
                  ? () => makePrimaryMut.mutate(id)
                  : undefined
              }
              makePrimaryBusy={
                makePrimaryMut.isPending && makePrimaryMut.variables === id
              }
            />
          ))}
        </div>
      )}

      {/* Attach — search the inbox and link another email to this card. Stays
          available after the first email so a card can carry many. */}
      {attaching ? (
        <div
          className={`rounded-lg border border-neutral-200 dark:border-zinc-800 ${
            hasLinked ? 'mt-1.5' : ''
          }`}
        >
          <div className="flex items-center gap-2 border-b border-neutral-200 px-2.5 py-2 dark:border-zinc-800">
            <Search className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subject / sender to attach…"
              className="min-w-0 flex-1 bg-transparent text-[12px] text-neutral-700 placeholder:text-neutral-400 focus:outline-none dark:text-neutral-200"
            />
            <button
              type="button"
              onClick={() => {
                setAttaching(false)
                setSearch('')
              }}
              aria-label="Cancel attach"
              className="shrink-0 rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-zinc-800"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="max-h-[280px] overflow-y-auto overscroll-contain">
            {searchQ.isLoading ? (
              <div className="space-y-1 p-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-12 animate-pulse rounded bg-neutral-100 dark:bg-zinc-800"
                  />
                ))}
              </div>
            ) : searchQ.isError ? (
              <p className="px-3 py-4 text-center text-[11.5px] text-neutral-500 dark:text-zinc-400">
                Couldn’t search emails. Try again.
              </p>
            ) : attachResults.length === 0 ? (
              <p className="px-3 py-6 text-center text-[11.5px] text-neutral-500 dark:text-zinc-400">
                {debounced
                  ? 'No new emails match. Try a different search.'
                  : 'Type to search your inbox for an email to attach.'}
              </p>
            ) : (
              <ul>
                {attachResults.map((item) => {
                  const busy = attachMut.isPending && attachMut.variables === item.id
                  return (
                    <li key={item.id} className="relative">
                      <EmailRow
                        email={toEmailMessage(item)}
                        category={NEUTRAL_CATEGORY}
                        isSelected={false}
                        isChecked={false}
                        selectable={false}
                        onSelect={() => {
                          if (attachMut.isPending) return
                          attachMut.mutate(item.id)
                        }}
                        onToggleSelect={swallow}
                        onToggleStar={swallow}
                      />
                      {busy && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-zinc-900/60">
                          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
          {attachMut.isError && (
            <p className="border-t border-neutral-200 px-3 py-1.5 text-[11px] text-rose-600 dark:border-zinc-800 dark:text-rose-400">
              Couldn’t attach that email. Please try again.
            </p>
          )}
        </div>
      ) : canSearch ? (
        <button
          type="button"
          onClick={() => setAttaching(true)}
          className={`flex w-full items-center gap-2 rounded-lg border border-dashed border-neutral-300 px-3 py-2.5 text-left text-[12px] text-neutral-500 transition-colors hover:border-blue-400 hover:bg-blue-50/40 hover:text-blue-700 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-blue-500/60 dark:hover:bg-blue-950/20 dark:hover:text-blue-300 ${
            hasLinked ? 'mt-1.5' : ''
          }`}
        >
          <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{hasLinked ? 'Attach another email' : 'Attach an email to this card'}</span>
        </button>
      ) : null}
    </section>
  )
}
